/*
 * @Author: auto-generated
 * @Date: 2024-06-29
 * @Description: 登录页面
 */
import { useEffect, useState } from 'react';
import CloudIcon from '@src/img/cloud.svg';
import { service } from '@src/utils/service';
import './index.css';

const LOGIN_OPTIONS = [
  {
    key: 'cloud',
    title: '使用腾讯云账号登录',
    desc: '',
    badge: '推荐使用'
  }
];

export default function Login({ history }) {
  const [activeProvider, setActiveProvider] = useState('cloud');
  const [loadingProvider, setLoadingProvider] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // 关闭成功弹框：清掉 URL 上的回调参数，恢复登录页初始状态
  const handleCloseSuccess = () => {
    setSuccessMsg('');
    if (window.history && window.history.replaceState) {
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState(null, '', cleanUrl);
    }
  };

  // 页面加载时：如果 URL 上带 code（OAuth 回调），自动完成 callback 流程
  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const code = search.get('code');
    const state = search.get('state');
    const signature = search.get('signature');
    if (!code) return;

    setLoadingProvider('cloud');
    service
      .tcOAuthCallback({ code, state, signature })
      .then((res) => {
        console.log('res', res);
        setSuccessMsg('授权登录成功，即将跳转…');
        if (res?.success && res?.redirect_url) {
          // 稍延后跳转，让用户看到成功提示
          setTimeout(() => {
            window.location.href = res.redirect_url;
          }, 1500);
        }
      })
      .catch((err) => {
        console.log('err', err);
        setErrorMsg(err?.response?.data?.message || err?.message || '登录失败，请重试');
      })
      .finally(() => {
        setLoadingProvider('');
      });
    // 只在挂载时执行一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloudLogin = async () => {
    setErrorMsg('');
    setLoadingProvider('cloud');
    try {
      const res = await service.getTcOAuthAuthorizeUrl();
      const url = res?.data?.authorize_url || res?.data?.url || res?.authorize_url || res?.url;
      if (!url) {
        throw new Error('未获取到授权地址');
      }
      // 优先从当前页面 URL 的 query 上取 state，取不到则兜底生成
      const currentSearch = new URLSearchParams(window.location.search);
      const state = currentSearch.get('state') || crypto.randomUUID();
      try {
        window.sessionStorage.setItem('tc_oauth_state', state);
      } catch (e) {
        // 忽略 sessionStorage 不可用的情况
      }
      // 若后端返回的 url 中已带 state 参数，则替换掉；否则追加
      let finalUrl;
      try {
        const urlObj = new URL(url, window.location.origin);
        urlObj.searchParams.set('state', state);
        finalUrl = urlObj.toString();
      } catch (e) {
        // url 不是标准格式时，做字符串兜底
        if (/([?&])state=[^&]*/.test(url)) {
          finalUrl = url.replace(/([?&])state=[^&]*/, `$1state=${encodeURIComponent(state)}`);
        } else {
          const separator = url.includes('?') ? '&' : '?';
          finalUrl = `${url}${separator}state=${encodeURIComponent(state)}`;
        }
      }
      window.location.href = finalUrl;
    } catch (err) {
      setLoadingProvider('');
      setErrorMsg(err?.response?.data?.message || err?.message || '获取授权地址失败');
    }
  };

  const handleProviderClick = (providerKey) => {
    if (activeProvider !== providerKey) {
      setActiveProvider(providerKey);
      return;
    }
    if (providerKey === 'cloud') {
      handleCloudLogin();
    }
  };

  return (
    <div className='login-page'>
      <div className='login-card'>
        <div className='login-brand-row'>
          <img src={CloudIcon} alt='CloudBase' className='login-brand-icon' />
          <span className='login-brand-text'>腾讯云</span>
        </div>

        <h1 className='login-title'>登录控制台</h1>

        <div className='login-option-list'>
          {LOGIN_OPTIONS.map((option) => {
            const isActive = activeProvider === option.key;
            const isLoading = loadingProvider === option.key;

            return (
              <button
                type='button'
                key={option.key}
                className={`login-option ${isActive ? 'active' : ''}`}
                onClick={() => handleProviderClick(option.key)}
                aria-pressed={isActive}
                disabled={!!loadingProvider}
              >
                <span className='login-option-main'>
                  <span className={`login-option-icon ${option.key}`}>
                    {option.key === 'cloud' ? (
                      <img src={CloudIcon} alt='腾讯云登录' className='login-option-cloud-icon' />
                    ) : (
                      <span className='login-option-wechat-glyph'>微</span>
                    )}
                  </span>

                  <span className='login-option-copy'>
                    <span className='login-option-title-row'>
                      <span className='login-option-title'>{option.title}</span>
                      {option.badge ? <span className='login-option-badge'>{option.badge}</span> : null}
                    </span>
                    {option.desc ? <span className='login-option-desc'>{option.desc}</span> : null}
                    {isLoading ? <span className='login-option-loading'>登录中...</span> : null}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {errorMsg ? <div className='login-error'>{errorMsg}</div> : null}

        <div className='login-footer'>
          还没有账号？
          <button
            type='button'
            className='login-register-link'
            onClick={() => window.open('https://cloud.tencent.com/login', '_blank', 'noopener,noreferrer')}
          >
            立即注册
          </button>
        </div>
      </div>

      {successMsg ? (
        <div className='login-modal-mask' onClick={handleCloseSuccess} role='presentation'>
          <div className='login-modal' onClick={(e) => e.stopPropagation()} role='dialog' aria-modal='true'>
            <div className='login-modal-icon' aria-hidden='true'>
              <svg viewBox='0 0 52 52' width='48' height='48'>
                <circle cx='26' cy='26' r='24' fill='#34c759' opacity='0.12' />
                <path
                  fill='none'
                  stroke='#34c759'
                  strokeWidth='3'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M16 27l7 7 13-14'
                />
              </svg>
            </div>
            <div className='login-modal-title'>授权成功</div>
            <div className='login-modal-desc'>{successMsg}</div>
            <button type='button' className='login-modal-btn' onClick={handleCloseSuccess}>
              确定
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
